import axios from 'axios';
import { UpdatedField } from './web-types/api';
import { URL_FOUNDATION } from '../../config/keys';
import { ImportCampaignAndPeriods, PivotColumn } from './import';
import { Period, Promotion } from './model';

export function getPromotions(body?: { descending: boolean, order_by: string }): Promise<Promotion[]> {
	return axios.post(`${URL_FOUNDATION}/api/v2/promotions/query`, body ?? {}).then(res => res.data);
}

export async function getPromotionById(promotion_id: number): Promise<Promotion> {
	return axios.get(`${URL_FOUNDATION}/api/v2/promotions/${promotion_id}`).then(res => res.data);
}

export type ProductFieldValue = {
	field_id: number,
	product_uuid: string,
	value: unknown,
}

// The latest value of each product custom field per product, for the
// client-side product filters of the promotions page.
// Memoized for the session: three consumers (promotion product picker on
// every popup open, products page, promotions listing) were re-running a
// heavy DISTINCT ON over field_values on the server each time. The data
// changes rarely (form submissions); a reload refreshes it.
let productFieldValuesCache: Promise<ProductFieldValue[]> | undefined;
export function getProductFieldValues(): Promise<ProductFieldValue[]> {
	productFieldValuesCache ??= axios.get(`${URL_FOUNDATION}/api/v2/forms/field_values/products`)
		.then(res => res.data)
		.catch(e => {
			// a failed fetch must not poison the session cache
			productFieldValuesCache = undefined;
			throw e;
		});
	return productFieldValuesCache;
}

export type PutPromotionBody = {
	name?: string,
	periods?: Period[],
	is_active?: boolean
	description?: UpdatedField<string>,
	companies_add?: number[],
	companies_del?: number[],
	products_add?: number[],
	products_del?: number[],
	fields?: UpdatedField<{ [key: string]: unknown }>
}

export function putPromotion(promotionId: number, body: PutPromotionBody): Promise<number> {
	return axios.put(`${URL_FOUNDATION}/api/v2/promotions/${promotionId}`, body).then(res => res.data);
}

// Upserts one per-product promotion field value (a field ticked both
// promotion and product) on the (promotion, product) link; a null value
// removes the key
export function putPromotionProductFields(promotionId: number, productId: number, body: { field_id: number, value: unknown }): Promise<void> {
	return axios.put(`${URL_FOUNDATION}/api/v2/promotions/${promotionId}/products/${productId}/fields`, body).then(res => res.data);
}

export type PostPromotionBody = {
	name: string,
	periods: Period[],
	is_active: boolean,
	companies: number[],
	description: string | undefined,
	products: number[]
}

export function postPromotion(body: PostPromotionBody): Promise<number> {
	return axios.post(`${URL_FOUNDATION}/api/v2/promotions`, body).then(res => res.data);
}

export function deletePromotion(promotionId: number): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/promotions/${promotionId}`).then(res => res.data);
}

export function importPromotionsCall(promotions: ImportCampaignAndPeriods[], company_field_mappings: { additional_column_id: number, field_id: number }[], pivot_column: PivotColumn | undefined): Promise<void> {
	return axios.post(`${URL_FOUNDATION}/api/v2/promotions/import`, { company_field_mappings, promotions, pivot_column }).then(res => res.data);
}

export function deletePromotionsById(promotionIds: number[]): Promise<void> {
	return axios.delete(`${URL_FOUNDATION}/api/v2/promotions`, { data: promotionIds }).then(res => res.data);
}
